import { Link, usePage } from "@inertiajs/react";
import axios from "axios";
import { memo, useEffect, useState } from "react";

import BB_Button from "@/Components/BB_Button";
import BB_ImageWrapper from "@/Components/BB_ImageWrapper";
import CheckmarkIcon from "@/Icons/checkmark.svg?react";
import SpinnerIcon from "@/Icons/spinner.svg?react";
import BB_PaginatorCursor from "@/types/BB_PaginatorCursor";
import BB_Product from "@/types/BB_Product";

export type BBQuickSelectCategoriesProps = {
	product: BB_Product;
	parent_product?: BB_Product | null;
	products: BB_Product[];
};

const BB_Select = (props: BBQuickSelectCategoriesProps) => {
	const { product_siblings } = usePage().props as {
		product_siblings?: BB_PaginatorCursor<BB_Product>;
	};

	const [siblings, setSiblings] = useState<BB_Product[]>(product_siblings?.data ?? []);
	const [siblingsNextCursor, setSiblingsNextCursor] = useState<string | null>(
		product_siblings?.next_cursor ?? null
	);
	const [isLoadingSiblings, setIsLoadingSiblings] = useState(false);

	const [hoverVariant, setHoverVariant] = useState<[BB_Product, boolean]>([props.product, true]);

	const loadMoreSiblings = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();
		if (isLoadingSiblings || !siblingsNextCursor) {
			return;
		}

		try {
			setIsLoadingSiblings(true);

			const nextPage = `/api/get/siblings/${props.product.id}?cursor=${siblingsNextCursor}`;
			const response = await axios.get(nextPage);
			const result = response.data;

			setIsLoadingSiblings(false);
			setSiblings([...siblings, ...result.data]);
			setSiblingsNextCursor(result.next_cursor);
		} catch (error) {
			console.error("[🧨] cannot load more siblings", error);
			setTimeout(() => setIsLoadingSiblings(false), 350);
		}
	};

	useEffect(() => {
		if (product_siblings) {
			props.product.image = props.product.images?.[0] ?? null;
			const updatedSiblings = [props.product, ...product_siblings.data];
			setSiblings(updatedSiblings);
		}
	}, [product_siblings]);

	return (
		<div className="flex flex-col gap-2">
			<div className={"relative"}>
				<div className="scrollbar-hide flex w-full gap-3 overflow-x-auto pr-14 pl-3 whitespace-nowrap select-none sm:px-6">
					<div
						className={
							"hover:border-primary-500 flex shrink-0 overflow-hidden rounded-lg border-2 border-gray-500"
						}>
						{props.parent_product && (
							<Link
								href={route("p", {
									b_id: props.parent_product.b_id,
									slug: props.parent_product.slug
								})}
								onMouseEnter={() =>
									props.parent_product &&
									setHoverVariant([props.parent_product, false])
								}
								onMouseLeave={() => setHoverVariant([props.product, true])}
								className="flex h-16 w-16 items-center justify-center bg-white font-medium text-gray-800!">
								ALLE
							</Link>
						)}
					</div>

					{siblings.map((product, index) => (
						<Link
							key={product.id}
							href={route("p", { b_id: product.b_id, slug: product.slug })}
							onMouseEnter={() => setHoverVariant([product, true])}
							onMouseLeave={() => setHoverVariant([props.product, true])}
							className={`flex h-17 w-17 shrink-0 items-center overflow-hidden rounded-lg border-2 border-gray-500 ${index === 0 && "border-primary-500!"} hover:border-primary-500 relative bg-white`}>
							{index === 0 && (
								<div>
									<div
										className={
											"bg-primary-500 absolute -top-11 -left-11 z-10 h-18 w-18 rotate-45"
										}></div>
									<CheckmarkIcon className="text-background-900 absolute -top-3.5 -left-3.5 z-10 h-12 w-12" />
								</div>
							)}
							{product.image ? (
								<BB_ImageWrapper
									bb_image={product.image}
									className="h-16 w-16 object-contain"
									suffix="?class=1"
									alt="Product"
								/>
							) : (
								<div
									className={
										"line-clamp-3 max-h-14 p-1 text-xs hyphens-auto whitespace-normal text-gray-600"
									}>
									{product.name}
								</div>
							)}
						</Link>
					))}

					{props.parent_product && siblingsNextCursor && (
						<div
							className={
								"hover:border-primary-500 flex shrink-0 overflow-hidden rounded-lg border-2 border-gray-500 bg-white"
							}>
							<BB_Button
								type={"custom"}
								onClick={loadMoreSiblings}
								buttonClassName={`flex justify-center items-center w-16 h-16 text-gray-800 font-medium cursor-pointer ${isLoadingSiblings && "pointer-events-none bg-none! text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"}`}
								icon={isLoadingSiblings ? SpinnerIcon : undefined}
								iconClassName={`h-5 w-5 ${isLoadingSiblings ? "pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500" : "mr-2"}`}
								title={isLoadingSiblings ? "" : "mehr"}
							/>
						</div>
					)}

					<div className="from-background-100 dark:from-dark-background-300 pointer-events-none absolute top-0 right-0 block h-full w-20 bg-gradient-to-l to-transparent" />
				</div>
			</div>
			<div className={"line-clamp-1 px-3 text-sm text-pretty sm:px-6"}>
				<span className={"font-medium"}>{hoverVariant[1] ? "Variante" : "Produkt"}:</span>
				<span className={"text-gray-600 dark:text-gray-300"}>
					&nbsp;{hoverVariant[0].name}
				</span>
			</div>
		</div>
	);
};

export default memo(BB_Select);
